// Sync engine — reenvía la cola de acciones (outbox), procesa la cola de sync
// (push) y hace pull paginado. Nunca pisa registros locales con cambios sin
// confirmar (_sync_state distinto de 'synced').
import { db, getMeta, setMeta, META, nowIso, getOrCreateDeviceId } from '@/lib/db';
import { syncPush, syncPull, submitAction, getAnimal, reconcileSummary, reconcileIds } from '@/lib/api';

const MAX_RETRIES = 5;
const MAX_PULL_LOOPS = 20;
const PULL_LIMIT = 2000;
const RECONCILE_MIN_INTERVAL_MS = 10 * 60 * 1000; // no más de 1 verificación cada 10 min automática
let lastReconcileAttemptAt = 0;

let running = false;
let listeners = new Set();

// ---------------------------------------------------------------------------
// Fix I (2026-09-17 — incidente 229 registros sin subir): cuando el token
// vence, el servidor responde 401 a TODA llamada de sync. Antes se seguía
// reintentando cada 30s en vano y la app mostraba "Sincronizando…" para
// siempre. Ahora el primer 401 marca la sesión como vencida, corta los
// reintentos y se avisa con un cartel. NUNCA se borra nada por esto.
// ---------------------------------------------------------------------------
let sessionExpired = false;

function isAuthError(e) {
  return e?.response?.status === 401;
}

export function isSessionExpired() {
  return sessionExpired;
}

export function markSessionExpired() {
  sessionExpired = true;
  notify({ phase: 'auth_expired', reason: 'Tu sesión venció. Volvé a iniciar sesión para subir tus registros pendientes.' });
}

// Se llama al re-autenticar correctamente: vuelven a habilitarse los
// reintentos automáticos y el indicador sale del estado trabado.
export function clearSessionExpired() {
  sessionExpired = false;
  notify({ phase: 'idle', reason: 'session_renewed' });
}

function notify(state) {
  listeners.forEach((cb) => {
    try { cb(state); } catch (_) {}
  });
}

export function onSyncState(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export async function getPendingCount() {
  const queueCount = await db.sync_queue.where('status').notEqual('done').count();
  const outboxCount = await db.actions_outbox.where('status').notEqual('done').count();
  return queueCount + outboxCount;
}

// Avisos proactivos +24h/+72h (Tanda 3 — Blindaje de datos): la fecha del
// registro pendiente más viejo en cualquiera de las dos colas, para poder
// avisar aunque la cantidad total sea chica (ej. 1 sola acción importante
// que quedó atascada varios días por algún error silencioso).
export async function getOldestPendingCreatedAt() {
  const [opsOldest] = await db.sync_queue.where('status').notEqual('done').sortBy('created_at');
  const [outboxOldest] = await db.actions_outbox.where('status').notEqual('done').sortBy('created_at');
  const dates = [opsOldest?.created_at, outboxOldest?.created_at].filter(Boolean);
  if (dates.length === 0) return null;
  return dates.sort()[0];
}

export async function getErrorCount() {
  const queueErr = await db.sync_queue.where('status').equals('error').count();
  const outboxErr = await db.actions_outbox.where('status').equals('error').count();
  return queueErr + outboxErr;
}

export async function getQueueDetails() {
  const ops = await db.sync_queue.where('status').notEqual('done').reverse().sortBy('created_at');
  const outbox = await db.actions_outbox.where('status').notEqual('done').reverse().sortBy('created_at');
  return { ops, outbox };
}

export async function retryQueueItem(id) {
  await db.sync_queue.update(id, { status: 'pending', retries: 0, last_error: null });
  if (typeof navigator === 'undefined' || navigator.onLine) syncNow();
}

export async function retryOutboxItem(id) {
  await db.actions_outbox.update(id, { status: 'pending', retries: 0, last_error: null });
  if (typeof navigator === 'undefined' || navigator.onLine) syncNow();
}

// Fix 4 (bug de UX — chip duplicado): "Descartar" quita una op con error de la
// cola sin reintentarla. Se usa cuando el usuario decide que ese registro ya
// no importa (ej. una carga de prueba vieja que quedó bloqueada).
export async function discardQueueItem(id) {
  await db.sync_queue.delete(id);
}
export async function discardOutboxItem(id) {
  await db.actions_outbox.delete(id);
}

// Fix 4 — "Resolver automáticamente": reintenta todo lo que quedó con error
// aplicando la lógica de auto-resolución del servidor (Fix 2: chip liberado /
// unificación de duplicados). Informa cuánto se resolvió solo y cuánto sigue
// necesitando revisión manual.
export async function resolveAllErrors() {
  const errorOps = await db.sync_queue.where('status').equals('error').toArray();
  const errorOutbox = await db.actions_outbox.where('status').equals('error').toArray();
  for (const o of errorOps) await db.sync_queue.update(o.id, { status: 'pending', retries: 0, last_error: null });
  for (const o of errorOutbox) await db.actions_outbox.update(o.id, { status: 'pending', retries: 0, last_error: null });
  const attempted = errorOps.length + errorOutbox.length;
  if (attempted === 0) return { attempted: 0, resolved: 0, stillErrored: 0 };
  await syncNow();
  const stillErrored = await getErrorCount();
  return { attempted, resolved: Math.max(0, attempted - stillErrored), stillErrored };
}

export async function queueOp({ entity, operation, entity_id, payload }) {
  await db.sync_queue.add({
    entity,
    operation,
    entity_id,
    payload,
    created_at: nowIso(),
    status: 'pending',
    retries: 0,
    last_error: null,
  });
  notify({ phase: 'queued' });
}

export async function queueActionOutbox(payload) {
  await db.actions_outbox.add({
    type: payload.type,
    payload,
    created_at: nowIso(),
    status: 'pending',
    retries: 0,
    last_error: null,
  });
  notify({ phase: 'queued' });
}

// ---------------------------------------------------------------------------
// 1) Reenviar acciones en outbox (motor de plantillas). La lógica de negocio
//    vive solo en el backend: acá solo reintentamos el POST original.
// ---------------------------------------------------------------------------
async function replayActionsOutbox() {
  const pending = await db.actions_outbox.where('status').equals('pending').limit(20).toArray();
  let replayed = 0;
  for (const item of pending) {
    try {
      await submitAction(item.payload);
      await db.actions_outbox.delete(item.id);
      replayed++;
    } catch (e) {
      // Sesión vencida: NO es un error de negocio de esta acción — se
      // propaga para cortar el drenado y avisar (Fix I).
      if (isAuthError(e)) throw e;
      if (e?.response) {
        // Error de negocio (4xx) — no bloquea el resto de la cola, queda visible.
        const retries = (item.retries || 0) + 1;
        await db.actions_outbox.update(item.id, {
          status: 'error',
          last_error: e.response?.data?.detail || 'No se pudo aplicar la acción',
          retries,
        });
      } else {
        // Error de red — probablemente se fue la señal de nuevo, cortamos el reintento.
        break;
      }
    }
  }
  return { replayed };
}

// ---------------------------------------------------------------------------
// 2) Empujar cola de sync (entidades: animal/farm/lot/event/inventory)
// ---------------------------------------------------------------------------
function reasonLabel(reason, chip) {
  if (reason === 'chip_exists') return `El chip ${chip || ''} ya está vinculado a otro animal`;
  return reason || 'Rechazado por el servidor';
}

// Fix 3 (prompt 2026-08-04b — unificación de duplicados no se aplicaba del
// todo): campos que el usuario puede haber cargado en el registro local
// "perdedor" (el que se unifica) y que NO deben perderse si el animal
// "ganador" del servidor todavía no los tiene.
const MERGE_BACKFILL_FIELDS = [
  'lomo_number', 'ear_tattoo', 'ear_tattoo_right', 'ear_tattoo_left', 'category',
  'observations', 'mother_id', 'mother_chip', 'mother_lomo', 'weight_kg', 'breed',
  // Fix C (2026-08-24): la foto del hierro también se conserva al unificar duplicados.
  'iron_brand', 'iron_brand_drawing', 'iron_brand_photo', 'photo_url',
];

function isEmptyVal(v) {
  return v === undefined || v === null || v === '';
}

function computeMergePatch(loserDoc, winnerDoc) {
  const patch = {};
  for (const f of MERGE_BACKFILL_FIELDS) {
    if (!isEmptyVal(loserDoc?.[f]) && isEmptyVal(winnerDoc?.[f])) patch[f] = loserDoc[f];
  }
  if (loserDoc?.is_sick && !winnerDoc?.is_sick) patch.is_sick = true;
  return patch;
}

// Reasigna cualquier operación en cola (eventos, acciones) que todavía
// apunte al id local "perdedor" de una unificación, para que no fallen al
// intentar aplicarse contra un animal que ya no existe (bug reportado: la
// unificación quedaba a mitad de camino y los eventos/acciones dependientes
// se perdían en error). Los eventos ya creados en el servidor con el
// animal_id viejo (viajaron en el MISMO batch que la creación del animal
// duplicado) se corrigen re-encolando un 'update' explícito — mutar solo la
// copia local no alcanza, porque el servidor ya los tiene con el id viejo.
async function reassignAnimalId(oldId, newId) {
  const evts = await db.events.where('animal_id').equals(oldId).toArray();
  for (const e of evts) {
    const now = nowIso();
    await db.events.update(e.id, { animal_id: newId, updated_at: now, _sync_state: 'pending_update', _local_updated_at: now });
    const updated = await db.events.get(e.id);
    if (updated) {
      const { _sync_state, _local_updated_at, ...payload } = updated;
      await queueOp({ entity: 'event', operation: 'update', entity_id: e.id, payload });
    }
  }

  const queued = await db.sync_queue.where('status').notEqual('done').toArray();
  for (const q of queued) {
    const patch = {};
    if (q.entity === 'animal' && q.entity_id === oldId) patch.entity_id = newId;
    if (q.payload && q.payload.animal_id === oldId) patch.payload = { ...q.payload, animal_id: newId };
    if (Object.keys(patch).length) await db.sync_queue.update(q.id, patch);
  }

  const outboxQueued = await db.actions_outbox.where('status').notEqual('done').toArray();
  for (const o of outboxQueued) {
    if (o.payload && o.payload.animal_id === oldId) {
      await db.actions_outbox.update(o.id, { payload: { ...o.payload, animal_id: newId } });
    }
  }
}

async function pushPending(batchSize = 200) {
  // Orden FIFO estricto (por fecha de encolado) para respetar dependencias
  // (ej. finca creada offline antes que sus animales).
  const allPending = await db.sync_queue.where('status').equals('pending').sortBy('created_at');
  const candidates = allPending.slice(0, batchSize);
  if (candidates.length === 0) return { pushed: 0, merged: 0 };
  // Fix H (2026-09-17): los lotes se arman por PESO además de por cantidad. Con fotos
  // de hierro (300-650 KB c/u), 200 operaciones podían superar los 20 MB: en la red de
  // una finca el envío no llegaba a completarse, la operación volvía a 'pending' y el
  // siguiente intento empezaba de cero. El cliente estuvo 3 días sin poder subir 207
  // registros por este motivo. Con lotes de ~2 MB cada envío se completa y el progreso
  // queda guardado.
  const MAX_BATCH_BYTES = 2 * 1024 * 1024;
  const pending = [candidates[0]]; // el primero siempre entra — si es pesado, viaja solo
  let bytes = JSON.stringify(candidates[0]).length;
  for (let i = 1; i < candidates.length; i++) {
    const size = JSON.stringify(candidates[i]).length;
    if (bytes + size > MAX_BATCH_BYTES) break;
    pending.push(candidates[i]);
    bytes += size;
  }
  const deviceId = await getOrCreateDeviceId();
  const operations = pending.map((p) => ({
    op_id: 'op-' + p.id,
    entity: p.entity,
    operation: p.operation,
    entity_id: p.entity_id,
    client_updated_at: p.created_at,
    payload: p.payload || {},
  }));
  const res = await syncPush({ deviceId, operations });
  let merged = 0;
  const mergedItems = [];
  for (let i = 0; i < pending.length; i++) {
    const p = pending[i];
    const r = res.results[i] || { status: 'error' };
    const table = entityToTable(p.entity);
    // Cualquier status distinto de 'applied'/'conflict_resolved' que el
    // servidor ya haya resuelto de forma segura (ej. 'merged') se trata como
    // ÉXITO y sale de la cola — nunca reintenta hasta agotar MAX_RETRIES.
    if (r.status === 'merged') {
      await db.sync_queue.update(p.id, { status: 'done', last_result: 'merged', retries: (p.retries || 0) + 1, last_error: null });
      // La reasignación de dependientes se procesa DESPUÉS de terminar este
      // batch entero (ver abajo) — si se hiciera acá mismo, un evento que
      // viajó en el mismo push y todavía no se procesó (índice > i) volvería
      // a pisar el animal_id corregido al marcarse 'synced' con su propio
      // resultado 'applied'.
      mergedItems.push({ p, r });
      merged++;
      continue;
    }
    if (r.status === 'applied' || r.status === 'conflict_resolved') {
      await db.sync_queue.update(p.id, {
        status: 'done', last_result: r.status, retries: (p.retries || 0) + 1, last_error: null,
      });
      if (table) {
        const local = await table.get(p.entity_id);
        if (local && local._sync_state !== 'synced') {
          await table.update(p.entity_id, { _sync_state: 'synced' });
        }
      }
    } else {
      const retries = (p.retries || 0) + 1;
      const isRejected = r.status === 'rejected';
      await db.sync_queue.update(p.id, {
        status: retries >= MAX_RETRIES || isRejected ? 'error' : 'pending',
        last_result: r.status,
        last_error: reasonLabel(r.reason, r.chip),
        retries,
      });
      if (isRejected && table) {
        const local = await table.get(p.entity_id);
        if (local) await table.update(p.entity_id, { _sync_state: 'conflict' });
      }
    }
  }

  // Recién ahora se reemplazan los animales unificados y se reasignan sus
  // dependientes — así esta corrección queda como la ÚLTIMA palabra sobre
  // esos registros dentro del batch, sin que la termine pisando el
  // procesamiento normal (arriba) de otra operación del mismo push.
  for (const { p, r } of mergedItems) {
    const table = entityToTable(p.entity);
    if (!table || !r.entity_id || r.entity_id === p.entity_id) continue;
    const loserDoc = await table.get(p.entity_id); // lo que el usuario cargó localmente
    await table.delete(p.entity_id).catch(() => {});
    let winnerDoc = null;
    try {
      winnerDoc = await getAnimal(r.entity_id);
      await table.put({ ...winnerDoc, _sync_state: 'synced', _local_updated_at: winnerDoc.updated_at });
    } catch (_) { /* se traerá igual en el próximo pull */ }
    if (p.entity === 'animal') {
      // No perder datos cargados en el registro perdedor que el ganador
      // todavía no tenga (lomo, tatuaje, categoría, madre, etc.).
      if (loserDoc && winnerDoc) {
        const patch = computeMergePatch(loserDoc, winnerDoc);
        if (Object.keys(patch).length > 0) {
          const now = nowIso();
          await table.update(r.entity_id, { ...patch, updated_at: now, _sync_state: 'pending_update', _local_updated_at: now });
          await queueOp({ entity: 'animal', operation: 'update', entity_id: r.entity_id, payload: { ...patch, updated_at: now } });
        }
      }
      // Eventos/acciones que aún apuntaban al id perdedor pasan a apuntar
      // al ganador — si no, quedarían huérfanos y fallarían después.
      await reassignAnimalId(p.entity_id, r.entity_id);
    }
  }
  return { pushed: pending.length, merged };
}

// ---------------------------------------------------------------------------
// Drenado rápido de la cola (Fix 2 — hotfix pérdida de datos 31/07/2026):
// antes se subía un solo batch de 50 cada 30s, lo que con cargas masivas
// (miles de altas) tardaba horas de app abierta para vaciarse. Ahora, cuando
// hay señal, se encadenan batches de 200 en loop continuo hasta vaciar la
// cola por completo — el intervalo de 30s queda solo como verificación de
// fondo, no como límite de velocidad. Progreso visible vía onSyncState.
// ---------------------------------------------------------------------------
async function drainPending() {
  const totalAtStart = await db.sync_queue.where('status').equals('pending').count();
  let totalPushed = 0;
  let totalMerged = 0;
  let consecutiveErrors = 0;
  let currentBatchSize = 200;
  while (true) {
    if (typeof navigator !== 'undefined' && !navigator.onLine) break;
    let res;
    try {
      // Fix H (2026-09-17): el tamaño del lote se adapta a la red. Ante un fallo
      // (típicamente un envío cortado por conexión lenta), el siguiente intento va
      // con la mitad de operaciones, hasta un mínimo de 1. Cada lote que SÍ entra
      // queda confirmado en el servidor: el progreso ya no se pierde entre intentos.
      res = await pushPending(currentBatchSize);
      consecutiveErrors = 0;
      if (currentBatchSize < 200) currentBatchSize = Math.min(200, currentBatchSize * 2);
    } catch (e) {
      // Fix I: un 401 no se reintenta ni se degrada el lote — la sesión venció
      // y hay que renovarla; la cola queda intacta esperando.
      if (isAuthError(e)) throw e;
      consecutiveErrors++;
      currentBatchSize = Math.max(1, Math.floor(currentBatchSize / 2));
      if (consecutiveErrors >= 5) break; // se reintentará en el próximo syncNow()
      await new Promise((r) => setTimeout(r, 1500 * consecutiveErrors));
      continue;
    }
    consecutiveErrors = 0;
    if (res.pushed === 0) break; // cola vacía
    totalPushed += res.pushed;
    totalMerged += res.merged || 0;
    const remaining = await db.sync_queue.where('status').equals('pending').count();
    notify({ phase: 'draining', progress: { pushed: totalPushed, total: totalAtStart, remaining } });
  }
  return { pushed: totalPushed, merged: totalMerged };
}

// ---------------------------------------------------------------------------
// 3) Pull paginado — jamás pisa un registro local con cambios sin confirmar.
//    Autoreparación: cada tanto (o si nunca se hizo), se fuerza un pull
//    completo (since=null) y se compara contra los registros locales
//    _sync_state:'synced'. Si alguno no aparece en el server (una sync que se
//    perdió alguna vez) se re-encola como alta — salvo que el server lo tenga
//    con deleted_at, en cuyo caso el pull normal ya respeta ese borrado.
// ---------------------------------------------------------------------------
const FULL_HEAL_INTERVAL_MS = 24 * 60 * 60 * 1000;

// Fix N (2026-10-06): una resincronización completa por equipo después de este
// cambio, para traer lo que el cursor viejo (hora del teléfono) se salteó.
const FULL_HEAL_VERSION = 'N';

async function shouldForceFullHeal() {
  if ((await getMeta('full_heal_version')) !== FULL_HEAL_VERSION) return true;
  const last = await getMeta(META.LAST_FULL_HEAL);
  if (!last) return true;
  return Date.now() - new Date(last).getTime() > FULL_HEAL_INTERVAL_MS;
}

// Fix P (2026-10-06): en un equipo de encargado/veterinario, lo que no es de su finca
// NO falta en el servidor: el servidor no se lo manda a propósito. Antes se re-subía
// como si se hubiera perdido: el 05/10 un teléfono que pasó del usuario del dueño al
// de un encargado re-subió en 33 s 184 animales de SAN ANTONIO, 17 fincas y 71 lotes,
// pisando la versión del servidor. Ahora se borra de este equipo y no se re-sube.
async function isOutOfScope(entity, rec) {
  const u = await getMeta(META.CURRENT_USER);
  if (!u || u.role === 'owner') return false;
  const farms = u.assigned_farm_ids || [];
  if (entity === 'farm') return !farms.includes(rec.id);
  if (entity === 'event') {
    const a = rec.animal_id ? await db.animals.get(rec.animal_id) : null;
    return !a || !farms.includes(a.farm_id);
  }
  return !!rec.farm_id && !farms.includes(rec.farm_id);
}

async function healMissingLocalRecords(serverIdsByEntity) {
  const entities = [
    ['animal', db.animals], ['farm', db.farms], ['lot', db.lots],
    ['event', db.events], ['inventory', db.inventories],
  ];
  let healed = 0;
  for (const [entity, table] of entities) {
    const serverIds = serverIdsByEntity[entity];
    if (!serverIds) continue;
    const localSynced = await table.where('_sync_state').equals('synced').toArray();
    for (const rec of localSynced) {
      // Fix N (2026-10-06): un registro borrado no se re-crea en el servidor.
      if (rec.deleted_at) continue;
      if (!serverIds.has(rec.id)) {
        if (await isOutOfScope(entity, rec)) { await table.delete(rec.id); continue; } // Fix P
        const { _sync_state, _local_updated_at, ...payload } = rec;
        await table.update(rec.id, { _sync_state: 'pending_create' });
        await queueOp({ entity, operation: 'create', entity_id: rec.id, payload });
        healed++;
      }
    }
  }
  return healed;
}

async function pullChanges() {
  let totalPulled = 0;
  let resetDetected = false;
  const forceFullHeal = await shouldForceFullHeal();
  if (forceFullHeal) await setMeta(META.SYNC_CURSOR, null);
  const isFullPull = forceFullHeal || !(await getMeta(META.SYNC_CURSOR));
  const serverIdsByEntity = isFullPull ? {} : null;
  for (let loop = 0; loop < MAX_PULL_LOOPS; loop++) {
    const since = await getMeta(META.SYNC_CURSOR);
    const data = await syncPull({ since });

    // Fix D (2026-08-24): si el dueño reinició el sistema, el servidor sube
    // data_epoch. Sin este chequeo, un dispositivo con la copia local vieja
    // volvería a SUBIR (push) los datos que se acaban de borrar a propósito,
    // deshaciendo el reinicio solo — por eso se descarta la copia local y la
    // cola pendiente ANTES de aplicar cualquier dato de esta respuesta.
    const serverEpoch = data.data_epoch || 0;
    const localEpoch = await getMeta(META.DATA_EPOCH, 0);
    if (serverEpoch > localEpoch) {
      // Antes de vaciar, nos fijamos si este dispositivo tenía algo que perder:
      // en el PRIMER reinicio de una cuenta, localEpoch también es 0 (nunca se
      // guardó todavía), así que "localEpoch > 0" no alcanza para distinguir
      // un dispositivo nuevo de uno que sí tenía datos y se los acaban de borrar.
      const hadLocalData = (await db.animals.count()) > 0 || (await db.sync_queue.where('status').notEqual('done').count()) > 0;
      await Promise.all([
        db.animals.clear(), db.events.clear(), db.inventories.clear(), db.inventory_scans.clear(),
        db.sync_queue.clear(), db.actions_outbox.clear(),
      ]);
      await setMeta(META.SYNC_CURSOR, null);
      await setMeta(META.DATA_EPOCH, serverEpoch);
      // Un dispositivo nuevo (IndexedDB limpia, sin datos ni cola) nunca tuvo
      // nada que perder — no le corresponde el aviso.
      if (localEpoch > 0 || hadLocalData) resetDetected = true;
      continue; // vuelve a pedir desde cero, con el cursor en null
    }

    let anyFull = false;
    for (const [entity, items] of Object.entries(data.data || {})) {
      const table = entityToTable(entity);
      if (isFullPull) {
        if (!serverIdsByEntity[entity]) serverIdsByEntity[entity] = new Set();
        items.forEach((it) => serverIdsByEntity[entity].add(it.id));
      }
      if (!table || items.length === 0) { if (items?.length >= PULL_LIMIT) anyFull = true; continue; }
      // Un solo bulkPut por entidad (en vez de N puts individuales) para evitar
      // disparar decenas de re-renders reactivos (useLiveQuery) en la misma
      // tanda, lo que puede romper componentes con animaciones SVG (recharts).
      const existingRows = await table.bulkGet(items.map((it) => it.id));
      const toPut = [];
      items.forEach((it, i) => {
        const existing = existingRows[i];
        // Regla de oro: un registro con cambios locales sin confirmar NUNCA
        // se pisa en el pull. El conflicto se resuelve en el push (LWW).
        if (!existing || existing._sync_state === 'synced') {
          // Fix N (2026-10-06): si la foto del hierro no vino (no cambió), se conserva
          // la local, salvo que el servidor diga que ya no hay foto o que es otra.
          const keepPhoto = !('iron_brand_photo' in it) && it.has_iron_brand_photo && existing?.iron_brand_photo
            && (existing.iron_brand_photo_at || null) === (it.iron_brand_photo_at || null);
          toPut.push({ ...it, ...(keepPhoto ? { iron_brand_photo: existing.iron_brand_photo } : {}), _sync_state: 'synced', _local_updated_at: it.updated_at });
        }
      });
      if (toPut.length) await table.bulkPut(toPut);
      totalPulled += items.length;
      if (items.length >= PULL_LIMIT) anyFull = true;
    }
    await setMeta(META.SYNC_CURSOR, data.cursor);
    if (!anyFull) break;
  }
  if (isFullPull) {
    await healMissingLocalRecords(serverIdsByEntity);
    await setMeta(META.LAST_FULL_HEAL, nowIso());
    await setMeta('full_heal_version', FULL_HEAL_VERSION);
  }
  return { pulled: totalPulled, resetDetected };
}

function entityToTable(entity) {
  return ({
    animal: db.animals,
    farm: db.farms,
    lot: db.lots,
    event: db.events,
    inventory: db.inventories,
  })[entity];
}

// ---------------------------------------------------------------------------
// Verificación de integridad dispositivo <-> servidor (§2 del pedido de
// resguardo). Dos pasos para escalar con miles de animales sin comparar ID
// por ID en cada apertura: primero cantidad + firma (hash) del conjunto de
// IDs; solo si no coincide se pide el detalle para saber cuáles faltan. Los
// registros borrados (papelera) se excluyen siempre — nunca generan avisos
// falsos de "faltante".
// ---------------------------------------------------------------------------
async function sha256Hex(str) {
  const enc = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function localIdsFor(entity) {
  const table = entityToTable(entity);
  if (!table) return [];
  const rows = await table.filter((r) => !r.deleted_at).toArray();
  return rows.map((r) => r.id);
}

async function requeueMissing(entity, ids) {
  const table = entityToTable(entity);
  if (!table) return 0;
  let requeued = 0;
  for (const id of ids) {
    const existingOp = await db.sync_queue.where('entity_id').equals(id)
      .and((o) => o.entity === entity && o.status !== 'done').first();
    if (existingOp) continue; // ya está en cola, no duplicar
    const rec = await table.get(id);
    if (!rec) continue;
    if (await isOutOfScope(entity, rec)) { await table.delete(id); continue; } // Fix P
    const { _sync_state, _local_updated_at, ...payload } = rec;
    const now = nowIso();
    await table.update(id, { _sync_state: 'pending_update', _local_updated_at: now });
    await queueOp({ entity, operation: 'update', entity_id: id, payload });
    requeued++;
  }
  return requeued;
}

// reconcile({force}) — si force=false (llamadas automáticas), se auto-limita
// a 1 verificación cada 10 minutos para no saturar con miles de animales.
export async function reconcile({ force = false } = {}) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return { skipped: 'offline' };
  const token = await getMeta(META.AUTH_TOKEN);
  if (!token) return { skipped: 'no_auth' };
  if (!force && Date.now() - lastReconcileAttemptAt < RECONCILE_MIN_INTERVAL_MS) return { skipped: 'throttled' };
  lastReconcileAttemptAt = Date.now();
  const summary = await reconcileSummary();
  const entities = {};
  let allMatch = true;
  let requeuedTotal = 0;
  for (const [entity, info] of Object.entries(summary.entities || {})) {
    const localIds = await localIdsFor(entity);
    const localHash = await sha256Hex(localIds.slice().sort().join(','));
    if (localHash === info.ids_hash) {
      entities[entity] = { local: localIds.length, server: info.count, match: true };
      continue;
    }
    allMatch = false;
    const detail = await reconcileIds(entity);
    const serverSet = new Set(detail.ids);
    const missingOnServer = localIds.filter((id) => !serverSet.has(id));
    entities[entity] = {
      local: localIds.length, server: info.count, match: false,
      missing_on_server: missingOnServer.length,
    };
    if (missingOnServer.length) {
      requeuedTotal += await requeueMissing(entity, missingOnServer);
    }
  }
  if (allMatch) await setMeta(META.LAST_RECONCILE_OK, nowIso());
  if (requeuedTotal > 0) syncNow();
  const verifiedAt = await getMeta(META.LAST_RECONCILE_OK);
  notify({ phase: 'idle', reconcile: { entities, allMatch, requeued: requeuedTotal } });
  return { entities, allMatch, requeued: requeuedTotal, verifiedAt };
}

export async function getLastReconcileOk() {
  return getMeta(META.LAST_RECONCILE_OK);
}

// Fix N (2026-10-06): las fotos de hierro ya no vienen en el pull. Las que este
// equipo no tiene se bajan de a 10 por ciclo, una por una, sin frenar nada.
async function backfillIronBrandPhotos(max = 10) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  const missing = await db.animals
    .filter((a) => a.has_iron_brand_photo && !a.iron_brand_photo && !a.deleted_at && a._sync_state === 'synced')
    .limit(max).toArray();
  for (const a of missing) {
    try {
      const full = await getAnimal(a.id);
      const cur = await db.animals.get(a.id);
      if (!cur || cur._sync_state !== 'synced' || cur.iron_brand_photo) continue;
      if (full?.iron_brand_photo) {
        await db.animals.update(a.id, { iron_brand_photo: full.iron_brand_photo, iron_brand_photo_at: full.iron_brand_photo_at || null });
      } else {
        await db.animals.update(a.id, { has_iron_brand_photo: false });
      }
    } catch (_) {
      break; // sin señal o error: se reintenta en el próximo ciclo
    }
  }
}

export async function syncNow() {
  if (running) return { skipped: true };
  running = true;
  notify({ phase: 'syncing' });
  try {
    const token = await getMeta(META.AUTH_TOKEN);
    if (!token) {
      notify({ phase: 'idle', reason: 'no auth' });
      return { skipped: true };
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      notify({ phase: 'offline' });
      return { skipped: true };
    }
    // Fix I: con la sesión vencida no se intenta nada — se re-avisa el estado
    // para que el cartel siga visible hasta que el usuario renueve la sesión.
    if (sessionExpired) {
      notify({ phase: 'auth_expired', reason: 'Tu sesión venció. Volvé a iniciar sesión para subir tus registros pendientes.' });
      return { skipped: true, reason: 'auth_expired' };
    }
    // Fix D bug (2026-08-31): el pull (con el chequeo de data_epoch) DEBE correr
    // ANTES del push. Si no, un dispositivo con cola pendiente durante un
    // reinicio del sistema sube (resucita) datos que el dueño acaba de borrar
    // a propósito, antes de llegar a enterarse de que hubo un reinicio.
    // Cuando se detecta un reinicio, pullChanges() ya vació sync_queue y
    // actions_outbox — no tiene sentido (ni es seguro) drenarlas después.
    // Fix N (2026-10-06): si la bajada falla (señal floja), igual se SUBE lo
    // pendiente; antes un pull cortado frenaba el ciclo entero y lo cargado en este
    // equipo no llegaba a los demás. Para no romper el chequeo de reinicio (Fix D) se
    // consulta solo el data_epoch con un pedido mínimo (since en el futuro = 0 datos).
    let pull;
    try {
      pull = await pullChanges();
    } catch (e) {
      if (isAuthError(e)) throw e;
      const probe = await syncPull({ since: '9999-12-31T23:59:59.999Z' });
      if ((probe.data_epoch || 0) > (await getMeta(META.DATA_EPOCH, 0))) throw e;
      pull = { pulled: 0, resetDetected: false, error: String(e?.message || e) };
    }
    const push = pull.resetDetected ? { pushed: 0, merged: 0, errors: 0 } : await drainPending();
    const outbox = pull.resetDetected ? { replayed: 0 } : await replayActionsOutbox();
    if (!pull.resetDetected) backfillIronBrandPhotos().catch(() => {});
    notify({ phase: 'idle', last: { outbox, push, pull, at: nowIso() } });
    // Verificación de integridad automática (throttleada a 1 cada 10 min) —
    // "al terminar cada sincronización completa" (§2).
    reconcile({ force: false }).catch(() => {});
    return { outbox, push, pull };
  } catch (e) {
    console.error('sync error', e);
    if (isAuthError(e)) {
      // Sesión vencida: NUNCA se limpian datos locales ni la cola por esto
      // (hotfix 31/07/2026 + Fix I) — se corta el reintento y se avisa.
      markSessionExpired();
    } else {
      notify({ phase: 'error', error: String(e?.message || e) });
    }
    return { error: String(e?.message || e) };
  } finally {
    running = false;
  }
}

// ---------------------------------------------------------------------------
// Reparar sincronización (manual, botón en Ajustes → Sincronización): re-sube
// TODOS los registros locales (upsert por id) y hace un pull completo. Pensado
// para arreglar YA dispositivos con datos rotos (animal solo-local, etc.).
// ---------------------------------------------------------------------------
export async function repairSync() {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { error: 'offline' };
  }
  // Fix N (2026-10-06): ANTES re-subía TODOS los registros con la hora actual. Se
  // usó 3 veces (20/09 y dos veces el 05/10): cada vez obligó a todos los equipos a
  // bajar la base entera, fotos incluidas, y podía pisar cambios más nuevos hechos
  // en otro equipo. Ahora: baja todo de nuevo sin pisar lo pendiente y re-sube SOLO
  // lo que el servidor no tiene.
  await setMeta(META.SYNC_CURSOR, null);
  await setMeta(META.LAST_FULL_HEAL, null);
  const rec = await reconcile({ force: true });
  const sync = await syncNow();
  return { reuploaded: rec?.requeued || 0, pushedTotal: sync?.push?.pushed || 0, pull: sync?.pull };
}

export function startAutoSync(intervalMs = 30000) {
  syncNow();
  const intervalId = setInterval(syncNow, intervalMs);
  const onOnline = () => syncNow();
  // Disparar drenado también al volver de background (Fix 2): una sesión de
  // carga en el corral se abre/cierra todo el tiempo, no solo al reconectar.
  const onVisible = () => { if (typeof document !== 'undefined' && document.visibilityState === 'visible') syncNow(); };
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
  }
  return () => {
    clearInterval(intervalId);
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    }
  };
}
